<template>
    <Card :title="$t('copyGroupProductsPage.title')">
        <WizardForm :steps="steps" @submit="createNewProductsGroup" ref="WizardForm"/>
    </Card>
</template>
<script>
import { defineAsyncComponent } from "vue";
import Card from "../../../../../components/new/CardComponent.vue";
import WizardForm from "../../../../../components/new/form/wizardForm/WizardForm.vue";

import { httpClient } from "../../../../../services/HttpClient";
import HttpSender from "../../../../../services/Http/HttpSender";
import HttpRequest from "../../../../../services/Http/HttpRequest";
import GeminiService from "../../../../../services/GeminiService/GeminiService";

const CopyGroupProductsGroupSettings = defineAsyncComponent(() => import('./steps/CopyGroupProductsGroupSettings.vue'));
const CopyGroupProductsImageSelection = defineAsyncComponent(() => import('./steps/CopyGroupProductsImageSelection.vue'));
const CopyGroupProductsAttributeSelection = defineAsyncComponent(() => import('./steps/CopyGroupProductsAttributeSelection.vue'));
const CopyGroupProductsContentAdaptation = defineAsyncComponent(() => import('./steps/CopyGroupProductsContentAdaptation.vue'));

export default {
    name: 'CopyGroupProductsPage',
    components: {
        Card, WizardForm
    },
    data() {
        return {
            integration: null,
            groupId: null,
            groupProducts: [],
            attributes: [],
            groupProductsImages: [],
            notVariableAttributes: [],
            variablesAttributes: [],
            initialNotVariableAttributesState: null,
            adaptedContentByProductUuid: {},
            geminiService: null,
        }
    },
    methods: {
        async fetchGroupProducts() {
            const limit = 100;
            const products = [];

            const firstResponse = await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                group_keys: [this.$route.params.groupId],
                extra_fields: ['integrationFilterItems'],
                page: 1,
                limit
            });
            products.push(...firstResponse.data.data);

            const totalPages = Math.ceil(firstResponse.data.meta.total / limit);
            for (let page = 2; page <= totalPages; page++) {
                const response = await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                    group_keys: [this.$route.params.groupId],
                    extra_fields: ['integrationFilterItems'],
                    page,
                    limit
                });
                products.push(...response.data.data);
            }

            this.groupProducts = products;
        },
        getProductsFilters() {
            let filtersByUuid = {};
            
            this.groupProducts.forEach(product => {
                product.integration_filter_items.forEach(filterItem => {
                    if (!filtersByUuid[filterItem.integration_filter.uuid]) {
                        filtersByUuid[filterItem.integration_filter.uuid] = {
                            uuid: filterItem.integration_filter.uuid,
                            name: filterItem.integration_filter.name,
                            names: filterItem.integration_filter.names,
                            isMultiple: Boolean(filterItem.integration_filter.is_multiple)
                        };
                    }
                })
            })

            return Object.values(filtersByUuid);
        },
        getNotVariableAttributes() {
            let notVariableAttributes = [];

            this.attributes.forEach(attribute => {
                let attributeValues = new Map();

                this.groupProducts.forEach(product => {
                    let filterItems = product.integration_filter_items.filter(item => item.integration_filter.uuid === attribute.uuid);
                    filterItems.forEach(filterItem => {
                        attributeValues.set(filterItem.name, filterItem);
                    });
                })

                if (attribute.isMultiple || attributeValues.size <= 1) {
                    notVariableAttributes.push({
                        ...attribute,
                        value: [...attributeValues.values().map(item => ({
                            uuid: item.uuid,
                            name: item.name,
                            names: item.names,
                            isDefault: Boolean(item.is_default)
                        }))]
                    });
                }
            })

            return notVariableAttributes;
        },
        getVariableAttributes() {
            let variableAttributes = [];

            this.attributes.forEach(attribute => {
                let attributeValues = new Set();

                this.groupProducts.forEach(product => {
                    let filterItems = product.integration_filter_items.filter(item => item.integration_filter.uuid === attribute.uuid);
                    filterItems.forEach(filterItem => {
                        attributeValues.add(filterItem.name);
                    });
                })

                if (!attribute.isMultiple && attributeValues.size > 1) {
                    variableAttributes.push(attribute);
                }
            })

            return variableAttributes;
        },
        getState() {
            let state = {};
            this.notVariableAttributes.forEach(a => {
                if (Array.isArray(a.value)) {
                    state[a.uuid] = a.value.map(v => ({ uuid: v.uuid, name: v.name })).sort((v1, v2) => v1.uuid.localeCompare(v2.uuid));
                } else {
                    state[a.uuid] = a.value ? { uuid: a.value.uuid, name: a.value.name } : null;
                }
            });
            return JSON.stringify(state);
        },
        fetchIntegration() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}`).then(response => {
                this.integration = response.data.data;
            })
        },
        buildAdaptationPayload() {
            const adaptationFields = ['names', 'descriptions', 'seo'];

            this.groupProducts.forEach(product => {
                adaptationFields.forEach(field => {
                    if (!this.adaptedContentByProductUuid[product.uuid]?.[field]?.[language]) {
                        if (!this.adaptedContentByProductUuid[product.uuid]) {
                            this.adaptedContentByProductUuid[product.uuid] = {};
                        }

                        if (!this.adaptedContentByProductUuid[product.uuid][field]) {
                            this.adaptedContentByProductUuid[product.uuid][field] = {};
                        }
                    }
                })
            })
        },
        async createNewProductsGroup() {
            let requests = [];

            this.groupProducts.forEach(product => {
                let requestBody = {
                    integration_category_uuid: product.category.uuid,
                    integration_uuid: this.$route.params.integrationUuid,
                    export_enabled: product.export_enabled,
                    sku: product.sku,
                    group_key: this.groupId,
                    measurement: product.measurement,
                    manufacturer: product.manufacturer,
                    balance: product.balance,
                    price: product.price,
                    sale_price: product.sale_price,
                    promo_price: product.promo_price,
                    images: this.groupProductsImages
                };

                const adapted = this.adaptedContentByProductUuid[product.uuid];
                const fields = ['names', 'descriptions', 'seo'];

                fields.forEach(field => {
                    const validValues = Object.fromEntries(
                        Object.entries(adapted[field] || {}).filter(([language, value]) => value && String(value).trim())
                    );

                    if (Object.keys(validValues).length) {
                        requestBody[field === 'seo' ? 'seos' : field] = validValues;
                    }
                });

                const notVariableItemUuids = this.notVariableAttributes.reduce((acc, attribute) => {
                    acc.push(...attribute.value.map(value => value.uuid));
                    return acc;
                }, []);

                const variableItemUuids = this.variablesAttributes.reduce((acc, attribute) => {
                    const productFilterItems = product.integration_filter_items.filter(item => item.integration_filter.uuid === attribute.uuid);
                    acc.push(...productFilterItems.map(item => item.uuid));
                    return acc;
                }, []);

                requestBody.filter_item_uuids = [...notVariableItemUuids, ...variableItemUuids];

                requests.push(new HttpRequest('POST', '/integrations/products', requestBody));
            })

            await new HttpSender().sendRequestsInBatches(requests, () => {})

            this.$router.push({ name: 'IntegrationProductsTable' })
        }
    },
    computed: {
        steps() {
            return [
                {
                    name: this.$t('copyGroupProductsPage.steps.groupSettings'),
                    component: CopyGroupProductsGroupSettings,
                    props: {
                        groupId: this.groupId,
                        groupProducts: this.groupProducts,
                        variablesAttributes: this.variablesAttributes,
                        'onUpdate:groupId': (value) => { this.groupId = value },
                        'onUpdate:isReadyForNext': () => { this.$refs.WizardForm?.refreshIsReadyForNext() }
                    }
                },
                {
                    name: this.$t('copyGroupProductsPage.steps.imageSelection'),
                    component: CopyGroupProductsImageSelection,
                    props: {
                        groupProductsImages: this.groupProductsImages,
                        'onUpdate:groupProductsImages': (value) => { this.groupProductsImages = value },
                        'onUpdate:isReadyForNext': () => { this.$refs.WizardForm?.refreshIsReadyForNext() }
                    }
                },
                {
                    name: this.$t('copyGroupProductsPage.steps.attributeSelection'),
                    component: CopyGroupProductsAttributeSelection,
                    props: {
                        notVariableAttributes: this.notVariableAttributes,
                        initialNotVariableAttributesState: this.initialNotVariableAttributesState,
                        'onUpdate:notVariableAttributes': (value) => { this.notVariableAttributes = value },
                        'onUpdate:isReadyForNext': () => { this.$refs.WizardForm?.refreshIsReadyForNext() }
                    }
                },
                {
                    name: this.$t('copyGroupProductsPage.steps.contentAdaptation'),
                    component: CopyGroupProductsContentAdaptation,
                    props: {
                        integration: this.integration,
                        groupProducts: this.groupProducts,
                        notVariableAttributes: this.notVariableAttributes,
                        initialNotVariableAttributesState: this.initialNotVariableAttributesState,
                        adaptedContentByProductUuid: this.adaptedContentByProductUuid,
                        geminiService: this.geminiService,
                        'onUpdate:isReadyForNext': () => { this.$refs.WizardForm?.refreshIsReadyForNext() }
                    }
                }
            ]
        }
    },
    async mounted() {
        await this.fetchGroupProducts();
        this.fetchIntegration();
        this.buildAdaptationPayload();
        this.attributes = this.getProductsFilters();
        this.notVariableAttributes = this.getNotVariableAttributes();
        this.variablesAttributes = this.getVariableAttributes();
        this.initialNotVariableAttributesState = this.getState();
        this.geminiService = new GeminiService();
    }
}
</script>